import React, {useEffect, useState} from 'react'
import {useNavigation, useCurrentRoute} from 'react-navi'
import {useTranslation} from 'react-i18next'

import {MainLayoutWithoutStretch, MainContent} from '../../../Layout/Layout'
import {HeaderTitle, HeaderRow, HeaderActions} from '../../../Molecules/ViewComponents/ViewHeader'
import DocumentList from '../DocumentList'
import {useAppState} from '../../../../state'

import {openInNewTab} from '../../../../config/utils'
import {Document} from '../../../../state/rest'
import {Helmet} from 'react-navi-helmet-async'
import {Loader} from '../../../../Routes'
import {ErrorMessage} from '../../../Molecules/Errors/ErrorMessage'
import {DocumentGroup} from 'state/documents/state'
import SearchRowWithResetButton from '../../../../Components/Molecules/SearchRowWithResetButton'
import {NotificationType, User, UserRole} from '../../../../state/state'
import {v4 as uuid} from 'uuid'
import ConfirmModal from '../../../Molecules/Modals/ConfirmModal'

const DocumentsView = () => {
  const nav = useNavigation()
  const {state, actions} = useAppState()
  const {t} = useTranslation('documents')
  const currentRoute = useCurrentRoute()
  const [pendingOpenDocumentId] = React.useState<string | null>(currentRoute.url.query?.open || null)
  const [searchQuery, setSearchQuery] = useState('')
  const [folderToDelete, setFolderToDelete] = useState<DocumentGroup | null>(null)
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null)

  React.useEffect(() => {
    if (currentRoute.url.query?.open) {
      nav.navigate('/documents', {replace: true})
    }
  }, [currentRoute.url.query?.open, nav])

  useEffect(() => {
    const setup = async () => {
      actions.v1.documents.getDocuments()
      actions.v1.documents.getDocumentFolders()
    }
    setup()
  }, [actions.v1.documents])

  const {
    documentGroups,
    documentsPending,
    documentFoldersPending,
    documentFoldersError,
    documentsError
  } = state.v1.documents

  const filterDG = (documentGroups: DocumentGroup[]) => {
    const userRights = state.me!.accessRights
    const isReadOnlyRole =
      state.me?.user.highestRole === 'BU' ||
      state.me?.user.highestRole === 'HI' ||
      state.me?.user.userToSite?.some(uts => uts.userRole === 'BU' || uts.userRole === 'HI')
    const hasWriteAccess =
      userRights?.write.chains.length > 0 ||
      userRights?.write.brands.length > 0 ||
      userRights?.write.groups.length > 0 ||
      userRights?.write.locations.length > 0 ||
      userRights?.write.sites.length > 0
    if (!isReadOnlyRole && (userRights?.superuser || hasWriteAccess)) {
      return documentGroups
    }
    return documentGroups.filter(
      folder => folder.documents.length > 0 || folder.type === 'EXTERNAL' || folder.createdBy?.id === state.me?.user.id
    )
  }

  const filteredDocumentGroups = filterDG((documentGroups as unknown) as DocumentGroup[])

  const accessRights = state.me?.accessRights
  const isReadOnlyRole =
    state.me?.user.highestRole === 'BU' ||
    state.me?.user.highestRole === 'HI' ||
    state.me?.user.userToSite?.some(uts => uts.userRole === 'BU' || uts.userRole === 'HI')
  const managerRoleRank: Record<string, number> = {CM: 5, BM: 4, GM: 3, LM: 2, SM: 1}
  const managerScope = (assignment: User['userToSite'][number]) => {
    switch (assignment.userRole as UserRole) {
      case 'CM':
        return assignment.chainId
      case 'BM':
        return assignment.brandId
      case 'GM':
        return assignment.locationGroupId
      case 'LM':
        return assignment.locationId
      case 'SM':
        return assignment.siteId
      default:
        return undefined
    }
  }
  const canManageCreatedItem = (createdBy?: Partial<User>, createdByRole?: string) => {
    if (!createdBy) return false
    if (!createdBy.userToSite) return false
    const creatorAssignments = createdBy.userToSite
    const creatorRole =
      createdByRole ||
      [...creatorAssignments].sort(
        (left, right) => (managerRoleRank[right.userRole] || 0) - (managerRoleRank[left.userRole] || 0)
      )[0]?.userRole
    const creatorRank = creatorRole ? managerRoleRank[creatorRole] : undefined
    if (!creatorRole || creatorRank === undefined) return false
    return Boolean(
      state.me?.user.userToSite.some(currentAssignment => {
        const currentRank = managerRoleRank[currentAssignment.userRole]
        if (currentRank === undefined || currentRank < creatorRank) return false
        if (currentRank > creatorRank) return true
        return (
          currentAssignment.userRole === creatorRole &&
          creatorAssignments.some(
            creatorAssignment =>
              creatorAssignment.userRole === creatorRole &&
              managerScope(currentAssignment) === managerScope(creatorAssignment)
          )
        )
      })
    )
  }
  const canManageDocuments =
    !isReadOnlyRole &&
    (accessRights?.superuser === true ||
      (accessRights?.write.sites.length ?? 0) > 0 ||
      (accessRights?.write.chains.length ?? 0) > 0)
  const canManageFolder = (folder: DocumentGroup) =>
    !isReadOnlyRole &&
    (accessRights?.superuser === true ||
      (accessRights?.write.chains.length ?? 0) > 0 ||
      (canManageCreatedItem(folder.createdBy, folder.createdByRole) &&
        (folder.documents.length === 0 ||
          folder.documents.some(document =>
            document.sites?.some(site => accessRights?.write.sites.includes(site.siteId))
          ))))

  const canManageDocument = (document: Document) =>
    !isReadOnlyRole &&
    (accessRights?.superuser === true ||
      (accessRights?.write.chains.length ?? 0) > 0 ||
      (canManageCreatedItem(document.createdBy, document.createdByRole) &&
        Boolean(document.sites?.some(site => accessRights?.write.sites.includes(site.siteId)))))

  const isLoading = documentsPending || documentFoldersPending
  const error = documentsError ? documentsError : documentFoldersError ? documentFoldersError : null

  const handleEdit = (id: string) => {
    nav.navigate('/documents/' + id)
  }

  const handleEditFolder = (id: string) => {
    nav.navigate('/documents/folders/' + id)
  }

  const handleDeleteFolderClick = (folder: DocumentGroup) => {
    setFolderToDelete(folder)
  }

  const handleDeleteFolder = async () => {
    if (!folderToDelete) {
      return
    }

    try {
      await actions.v1.documents.deleteDocumentFolder(folderToDelete.id)
      await Promise.all([actions.v1.documents.getDocuments(), actions.v1.documents.getDocumentFolders()])
      actions.addNotification({
        id: uuid(),
        type: NotificationType.INFO,
        title: t('documents:msg.deleteFolderSuccess', 'Folder deleted'),
        description: t('documents:msg.deleteFolderSuccessDescription', 'The folder and its files were deleted.'),
        visible: true,
        hideAfterDelay: 4000
      })
    } catch (error) {
      console.error('Failed to delete document folder', error)
      actions.addNotification({
        id: uuid(),
        type: NotificationType.ERROR,
        title: t('documents:msg.deleteFolderError', 'Could not delete folder'),
        description: t('documents:msg.deleteFolderErrorDescription', ''),
        visible: true,
        hideAfterDelay: 6000
      })
    } finally {
      setFolderToDelete(null)
    }
  }

  const handleDeleteDocumentClick = (document: Document) => {
    setDocumentToDelete(document)
  }

  const handleDeleteDocument = async () => {
    if (!documentToDelete) {
      return
    }

    try {
      await actions.v1.documents.deleteDocument(documentToDelete.id)
      actions.addNotification({
        id: uuid(),
        type: NotificationType.INFO,
        title: t('documents:msg.deleteDocumentSuccess', 'Document deleted'),
        description: t('documents:msg.deleteDocumentSuccessDescription', 'The file was deleted.'),
        visible: true,
        hideAfterDelay: 4000
      })

      try {
        await actions.v1.documents.getDocuments()
      } catch (refreshError) {
        console.error('Document deleted, but refreshing documents failed', refreshError)
        actions.addNotification({
          id: uuid(),
          type: NotificationType.ERROR,
          title: t('documents:msg.refreshDocumentsError', 'File deleted, but list refresh failed'),
          description: t(
            'documents:msg.refreshDocumentsErrorDescription',
            'Please refresh the page to see the latest document list.'
          ),
          visible: true,
          hideAfterDelay: 6000
        })
      }
    } catch (error) {
      console.error('Failed to delete document', error)
      actions.addNotification({
        id: uuid(),
        type: NotificationType.ERROR,
        title: t('documents:msg.deleteDocumentError', 'Could not delete document'),
        description: t('documents:msg.deleteDocumentErrorDescription', ''),
        visible: true,
        hideAfterDelay: 6000
      })
    } finally {
      setDocumentToDelete(null)
    }
  }

  const handleAddNewFile = () => {
    if (state.v1.documents.internalDocumentFolders.length === 0) {
      actions.addNotification({
        id: uuid(),
        type: NotificationType.ERROR,
        title: t('documents:msg.fileRequiresFolder', 'File cannot be added without a folder.'),
        visible: true,
        hideAfterDelay: 6000
      })
      return
    }

    nav.navigate('/create/document')
  }

  const handleDownload = async (d: Document) => {
    if (d.url) {
      openInNewTab(d.url)
    } else if (d.asset && d.asset.id) {
      try {
        const data = await actions.getDownloadUrl(d.asset.id)
        openInNewTab(data.downloadURL)
      } catch (error) {
        // TODO display note about failure
      }
    }
  }

  return (
    <MainLayoutWithoutStretch>
      <Helmet title={t('common:routes.documents', 'Documents')} />
      <Loader show={isLoading} />
      <ConfirmModal
        heading={t('documents:confirmation.deleteFolderTitle', 'Delete folder?')}
        content={t(
          'documents:confirmation.deleteFolderDescription',
          'Are you sure you want to delete the folder? If you choose to delete, all the files under the folder will be deleted too:'
        )}
        confirm={t('documents:confirmation.deleteFolderConfirm', 'I confirm I want to delete this folder')}
        actions={[
          {
            label: t('common:actions.cancel', 'Cancel'),
            action: () => setFolderToDelete(null)
          },
          {
            label: t('common:actions.delete', 'Delete'),
            action: () => {
              handleDeleteFolder()
            }
          }
        ]}
        isOpen={!!folderToDelete}
        closeModal={() => setFolderToDelete(null)}
      />
      <ConfirmModal
        heading={t('documents:confirmation.deleteDocumentTitle', 'Delete file?')}
        content={t('documents:confirmation.deleteDocumentDescription', 'Are you sure you want to delete the file?')}
        confirm={t('documents:confirmation.deleteDocumentConfirm', 'I confirm I want to delete this file')}
        actions={[
          {
            label: t('common:actions.cancel', 'Cancel'),
            action: () => setDocumentToDelete(null)
          },
          {
            label: t('common:actions.delete', 'Delete'),
            action: () => {
              handleDeleteDocument()
            }
          }
        ]}
        isOpen={!!documentToDelete}
        closeModal={() => setDocumentToDelete(null)}
      />
      {!isLoading && (
        <MainContent variant="white">
          <HeaderRow
            description={t(
              'documents:messages.viewDescription',
              'In documents view you can find work related documents in folders. You can add new folders, and add and edit folders with the toolbar on top, if your usage permissions allow.'
            )}
          >
            <HeaderTitle>{t('documents:labels.documentsTitle', 'Documents')}</HeaderTitle>
            {state.me &&
              (state.me.accessRights.write.sites.length > 0 || state.me.accessRights.write.chains.length > 0) && (
                <HeaderActions
                  onAdd={handleAddNewFile}
                  addLabel={t('documents:actions.addNewFile', 'Add New File')}
                  onAddSecondary={() => nav.navigate('/create/documentFolder')}
                  addSecondaryLabel={t('documents:actions.addNewFolder', 'Add new folder')}
                />
              )}
          </HeaderRow>
          <SearchRowWithResetButton
            placeholderText={t('documents:labels.searchPlaceHolder', 'Search Documents')}
            setSearchQuery={setSearchQuery}
          />
          {error === null ? (
            <DocumentList
              documentGroups={filteredDocumentGroups}
              onClick={handleDownload}
              onConfigure={canManageDocuments ? handleEdit : undefined}
              onConfigureFolder={handleEditFolder}
              onDeleteFolder={handleDeleteFolderClick}
              canManageFolder={canManageFolder}
              onDeleteDocument={canManageDocuments ? handleDeleteDocumentClick : undefined}
              canManageDocument={canManageDocuments ? canManageDocument : undefined}
              openDocumentId={!isLoading && filteredDocumentGroups.length > 0 ? pendingOpenDocumentId : null}
              key={pendingOpenDocumentId || 'default'}
              searchQuery={searchQuery}
            />
          ) : (
            <ErrorMessage message={error.message} />
          )}
        </MainContent>
      )}
    </MainLayoutWithoutStretch>
  )
}

export default DocumentsView
